import type { LeaderElector } from 'broadcast-channel'
import { BroadcastChannel, createLeaderElection } from 'broadcast-channel'
import type { UserStreamEvent } from 'sprites-types/api/v2/event-stream'

type EventCallback<T extends UserStreamEvent['eventType']> = (
  event: Extract<UserStreamEvent, { eventType: T }>
) => void

type AnyEventCallback = (event: UserStreamEvent) => void

export interface EventStreamService {
  connect: () => void
  disconnect: () => void
  on: <T extends UserStreamEvent['eventType']>(eventType: T, callback: EventCallback<T>) => void
  off: <T extends UserStreamEvent['eventType']>(eventType: T, callback: EventCallback<T>) => void
  isConnected: () => boolean
  isLeader: () => boolean
}

function createEventStreamServiceImplementation(): EventStreamService {
  const callbacksByType = new Map<string, Set<AnyEventCallback>>()
  let eventSource: EventSource | null = null
  let isConnectedState = false
  let reconnectTimeout: NodeJS.Timeout | null = null
  let channel: BroadcastChannel<UserStreamEvent> | null = null
  let elector: LeaderElector | null = null

  const hasAnyCallbacks = (): boolean => {
    for (const callbacks of callbacksByType.values()) {
      if (callbacks.size > 0) return true
    }
    return false
  }

  const notifyCallbacks = (event: UserStreamEvent) => {
    const callbacks = callbacksByType.get(event.eventType)
    if (!callbacks) return
    for (const callback of callbacks) {
      try {
        callback(event)
      } catch (error) {
        console.error('[EventStreamService] Error in callback:', error)
      }
    }
  }

  const connectSSE = () => {
    if (eventSource) return

    const url = `${process.env.NEXT_PUBLIC_BACKEND_API_ENDPOINT_URI}/v2/events/stream`
    eventSource = new EventSource(url, { withCredentials: true })

    eventSource.addEventListener('connected', () => {
      isConnectedState = true
      if (reconnectTimeout) {
        clearTimeout(reconnectTimeout)
        reconnectTimeout = null
      }
    })

    eventSource.addEventListener('user_event', (event) => {
      try {
        const data: UserStreamEvent = JSON.parse(event.data)
        notifyCallbacks(data)
        // Forward to non-leader tabs via BroadcastChannel
        void channel?.postMessage(data)
      } catch (error) {
        console.error('[EventStreamService] Error parsing event:', error)
      }
    })

    eventSource.addEventListener('error', () => {
      isConnectedState = false
      if (eventSource && eventSource.readyState === EventSource.CLOSED) return
      if (!reconnectTimeout && elector?.isLeader) {
        reconnectTimeout = setTimeout(() => {
          reconnectTimeout = null
          if (!isConnectedState && hasAnyCallbacks()) {
            disconnectSSE()
            connectSSE()
          }
        }, 5000)
      }
    })
  }

  const disconnectSSE = () => {
    if (reconnectTimeout) {
      clearTimeout(reconnectTimeout)
      reconnectTimeout = null
    }
    if (eventSource) {
      eventSource.close()
      eventSource = null
      isConnectedState = false
    }
  }

  const connect = () => {
    if (channel) return

    channel = new BroadcastChannel<UserStreamEvent>('event_stream')

    // Non-leader tabs receive forwarded events from the leader
    channel.onmessage = (event: UserStreamEvent) => {
      if (!elector?.isLeader) {
        notifyCallbacks(event)
      }
    }

    elector = createLeaderElection(channel)

    // awaitLeadership() resolves when this tab wins the election.
    // The library handles re-election automatically on tab close/crash via heartbeat.
    elector.awaitLeadership().then(() => {
      console.log('[EventStreamService] Elected as leader')
      connectSSE()
    })
  }

  const disconnect = () => {
    disconnectSSE()
    if (elector) {
      void elector.die()
      elector = null
    }
    if (channel) {
      void channel.close()
      channel = null
    }
  }

  const on = <T extends UserStreamEvent['eventType']>(eventType: T, callback: EventCallback<T>) => {
    let callbacks = callbacksByType.get(eventType)
    if (!callbacks) {
      callbacks = new Set()
      callbacksByType.set(eventType, callbacks)
    }
    callbacks.add(callback as AnyEventCallback)
    if (!channel) {
      connect()
    }
  }

  const off = <T extends UserStreamEvent['eventType']>(
    eventType: T,
    callback: EventCallback<T>
  ) => {
    const callbacks = callbacksByType.get(eventType)
    if (callbacks) {
      callbacks.delete(callback as AnyEventCallback)
    }
    if (!hasAnyCallbacks()) {
      disconnect()
    }
  }

  const isConnected = () => isConnectedState || (channel !== null && !elector?.isLeader)
  const isLeader = () => elector?.isLeader ?? false

  return {
    connect,
    disconnect,
    on,
    off,
    isConnected,
    isLeader,
  }
}

// Singleton instance
let globalEventStreamService: EventStreamService | null = null

export function createEventStreamService(): EventStreamService {
  if (!globalEventStreamService) {
    globalEventStreamService = createEventStreamServiceImplementation()
  }
  return globalEventStreamService
}

export function getEventStreamService(): EventStreamService {
  return createEventStreamService()
}
